import { Button, Card, Col, Popconfirm, Row } from "antd";
import { useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumbs from "../../components/layout/BreadCrumbs";
import PageLayout from "../../components/layout/PageLayout/PageLayout";
import BadgeConstatsCount from "../../components/tables/BadgeConstatsCount";
import TableConstats from "../../components/tables/TableConstats/TableConstats";
import Br from "../../components/ui/Br";
import spacing from "../../components/ui/spacingConfig";
import {
  useFetchAuditQuery,
  useLazyFetchAuditConstatsQuery,
} from "../../features/api/apiSlice";
import { useCrudAudit } from "../../features/api/crud/hooks/useCrudAudit";
import { useCrudConstat } from "../../features/api/crud/hooks/useCrudConstat";
import { useRole } from "../../features/roles/useRole";
import styles from "./PageAudit.module.scss";
import CardDescriptionAudit from "./components/CardDescriptionAudit";
import CardDetailsAudit from "./components/CardDetailsAudit";

const PageAudit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const auditId = Number(id);
  const { isAdmin } = useRole();

  const {
    data: auditData,
    isFetching,
    refetch: refetchAudit,
  } = useFetchAuditQuery(auditId, {
    skip: !auditId,
  });

  const { onClose: onCloseAudit, isClosing: isClosingAudit } = useCrudAudit({
    fetchAudit: refetchAudit,
  });

  const [fetchConstats, { data: constatsData, isFetching: isFetchingConstats }] =
    useLazyFetchAuditConstatsQuery();

  const fetchConstatsForTable = useCallback(
    (queryParams?: string) => {
      if (!auditId) return;
      fetchConstats({ auditId, queryParams });
    },
    [fetchConstats, auditId],
  );

  const { onDelete: onDeleteConstat } = useCrudConstat({
    fetchConstats: () => fetchConstatsForTable(),
  });

  const onCreateConstat = useCallback(() => {
    navigate(`/constats/new?auditId=${auditId}`);
  }, [navigate, auditId]);

  const navigateToEdit = useCallback(() => {
    navigate(`/audits/${auditId}/edit`);
  }, [navigate, auditId]);

  return (
    <PageLayout pageContainerClassName={styles.root} isLoading={isFetching}>
      <Row justify="space-between" align="middle">
        <Col>
          <Breadcrumbs
            pageName="audit"
            auditId={auditData?.id}
            auditName={auditData?.title}
          />
        </Col>
        <Col>
          <Row gutter={spacing.m}>
            <Col>
              <Button type="primary" onClick={navigateToEdit}>
                Modifier
              </Button>
            </Col>
            {isAdmin && (
              <Col>
                <Popconfirm
                  title="Confirmez-vous la validation de cet audit ?"
                  onConfirm={() => onCloseAudit(auditData?.id || 0)}
                  okText="Oui, valider"
                  cancelText="Annuler"
                >
                  <Button
                    type="primary"
                    disabled={
                      auditData?.status === "VALIDATED" || isClosingAudit
                    }
                    loading={isClosingAudit}
                  >
                    Valider l&apos;audit
                  </Button>
                </Popconfirm>
              </Col>
            )}
          </Row>
        </Col>
      </Row>
      <Br height={spacing.sm} />
      <Row gutter={spacing.m}>
        <Col span={14}>
          <CardDescriptionAudit auditData={auditData} />
        </Col>
        <Col span={10}>
          <CardDetailsAudit auditData={auditData} />
        </Col>
      </Row>
      <Br height={spacing.xl} />
      <Card
        title="Suivi des Constats"
        bordered={false}
        extra={
          <Button type="primary" onClick={onCreateConstat}>
            CrÃ©er un constat
          </Button>
        }
      >
        {auditData && (
          <>
            <Row>
              <BadgeConstatsCount
                constatsCount={auditData.constatsCount}
                constatsClosedCount={auditData.constatsClosedCount}
              />
            </Row>
            <Br height={spacing.sm} />
          </>
        )}
        <Row>
          <TableConstats
            data={constatsData}
            fetchData={fetchConstatsForTable}
            isLoading={isFetchingConstats}
            onDelete={onDeleteConstat}
          />
        </Row>
      </Card>
    </PageLayout>
  );
};

export default PageAudit;
